import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Zap, Target, Lock, TrendingUp, CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const { user, isPending, redirectToLogin } = useAuth();

  useEffect(() => {
    if (user && !isPending) {
      navigate('/dashboard');
    }
  }, [user, isPending, navigate]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <nav className="border-b border-slate-800/50 backdrop-blur-xl bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/20">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Vigilant</h1>
              <p className="text-xs text-slate-400">Revenue Protector</p>
            </div>
          </div>
          <button
            onClick={redirectToLogin}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all"
          >
            Sign In with Google
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-400 font-medium">AI-Powered Revenue Protection</span>
          </div>
          
          <h1 className="text-6xl font-bold text-white mb-6 leading-tight">
            Stop Revenue Leakage<br />
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Before It Happens
            </span>
          </h1>
          
          <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10">
            Vigilant automatically detects duplicate billing, subscription creep, and overcharges in your invoices—recovering money while you sleep.
          </p>

          <div className="flex items-center justify-center gap-4">
            <button
              onClick={redirectToLogin}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-lg font-medium text-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/20 flex items-center gap-2"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5" />
            </button>
            <button className="bg-slate-800/50 border border-slate-700/50 text-white px-8 py-4 rounded-lg font-medium text-lg hover:bg-slate-800 transition-all">
              Watch Demo
            </button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 hover:border-cyan-500/30 transition-all">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-cyan-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Automated Detection</h3>
            <p className="text-slate-400">
              AI-powered scanning identifies duplicate billing, overcharges, and subscription creep across all your vendors.
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 hover:border-cyan-500/30 transition-all">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Instant Recovery</h3>
            <p className="text-slate-400">
              Automated payment holds and ACH reversals through Moov integration recover funds immediately.
            </p>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 hover:border-cyan-500/30 transition-all">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-green-500/20 rounded-lg flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-3">Bank-Grade Security</h3>
            <p className="text-slate-400">
              Plaid integration ensures secure bank connections with enterprise-level encryption and compliance.
            </p>
          </div>
        </div>

        {/* Trust Section */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/50 rounded-2xl p-12 mb-20">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Enterprise-Grade Protection
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              'SOC 2 Type II Certified',
              'Bank-level encryption',
              'Real-time fraud detection',
              'Automated compliance',
              '99.9% uptime SLA',
              '24/7 support',
            ].map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                <span className="text-slate-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="text-center mb-20">
          <h2 className="text-3xl font-bold text-white mb-4">Simple, Transparent Pricing</h2>
          <p className="text-slate-400 mb-12">Start free. Scale as you grow.</p>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { name: 'Trial', price: 'Free', features: ['Up to 10 invoices/mo', 'Basic detection', '7-day trial'] },
              { name: 'Starter', price: '$49', features: ['Up to 100 invoices/mo', 'All detection types', 'Email support'] },
              { name: 'Pro', price: '$199', features: ['Unlimited invoices', 'Priority support', 'API access'], highlight: true },
              { name: 'Enterprise', price: 'Custom', features: ['Dedicated account', 'Custom integrations', 'SLA guarantee'] },
            ].map((tier) => (
              <div
                key={tier.name}
                className={`bg-slate-800/50 border rounded-xl p-6 ${
                  tier.highlight ? 'border-cyan-500/50 shadow-lg shadow-cyan-500/10' : 'border-slate-700/50'
                }`}
              >
                <h3 className="text-lg font-bold text-white mb-2">{tier.name}</h3>
                <div className="text-3xl font-bold text-white mb-4">
                  {tier.price}
                  {tier.price !== 'Free' && tier.price !== 'Custom' && <span className="text-sm text-slate-400">/mo</span>}
                </div>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature) => (
                    <li key={feature} className="text-sm text-slate-400 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={redirectToLogin}
                  className={`block w-full py-2 rounded-lg font-medium transition-all ${
                    tier.highlight
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700'
                      : 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Protect Your Revenue?
          </h2>
          <p className="text-slate-400 mb-8 text-lg">
            Join thousands of companies saving millions on erroneous charges.
          </p>
          <button
            onClick={redirectToLogin}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-lg font-medium text-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg shadow-cyan-500/20"
          >
            Start Free Trial
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-sm text-slate-500">
          © 2024 Vigilant Revenue Protector. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
